import { useCallback, useEffect, useState } from 'react';

import { Base64 } from 'js-base64';

import Heading from '@atlaskit/heading';
import { Box, Stack, Text } from '@atlaskit/primitives';

import { useCloudStatus } from '../../hooks/use-cloud-status';
import ErrorView from '../error/error-view';

import { useOnboardingData } from './utilities/save-data';

import type { OnboardingData } from './utilities/types';

const buildLoomRedirectUrl = (
	isProduction: boolean,
	cloudId: string,
	onboardingData: OnboardingData,
): string => {
	const searchParams = new URLSearchParams(window.location.search);
	const loomSource = searchParams.get('source');

	const params = new URLSearchParams();

	// For signup attribution
	if (loomSource) {
		params.set('source', loomSource);
	}

	// Add onboarding data
	params.set('data', Base64.encode(JSON.stringify({ 'onboarding-data': onboardingData })));

	// Send off!
	if (isProduction) {
		params.set('redirect_after', `https://www.loom.com/workspace/cloud/${cloudId}`);
		return `https://loom.com/api/auth/atlassian?${params.toString()}`;
	}

	params.set('redirect_after', `https://stage.loom.com/workspace/cloud/${cloudId}`);
	return `https://stage.loom.com/api/auth/atlassian?${params.toString()}`;
};

type Screen = 'provisioning' | 'timeout' | 'error';

export const AfterOnboardingStep = () => {
	const { createCloudStatus, cloudId, isProduction } = useCloudStatus();
	const { clearOnboardingData } = useOnboardingData();
	const [screen, setScreen] = useState<Screen>('provisioning');
	const [hasRedirected, setHasRedirected] = useState(false);

	const sendToLoom = useCallback(async () => {
		// Pull the final onboarding snapshot out of localStorage and clear it
		// so a subsequent signup on the same browser starts fresh.
		const onboardingData = clearOnboardingData();
		const url = buildLoomRedirectUrl(isProduction, cloudId, onboardingData);
		window.location.assign(url);
	}, [clearOnboardingData, cloudId, isProduction]);

	useEffect(() => {
		if (hasRedirected) {
			return;
		}

		switch (createCloudStatus) {
			case 'success':
				// Provisioning finished — hand off to Loom.
				setHasRedirected(true);
				void sendToLoom();
				return;
			case 'timeout':
			case 'timeout-recoverable':
				setScreen('timeout');
				return;
			case 'error':
			case 'error-unrecoverable':
				setScreen('error');
				return;
			case 'pending':
			default:
				setScreen('provisioning');
				return;
		}
	}, [createCloudStatus, hasRedirected, sendToLoom]);

	if (screen === 'timeout') {
		return (
			<Box testId="after-onboarding-step-timeout">
				<Stack space="space.150" alignInline="center">
					<Heading size="large">This is taking longer than expected</Heading>
					<Text>
						We&apos;re still setting up your workspace. You can wait a moment or refresh the page to
						try again.
					</Text>
				</Stack>
			</Box>
		);
	}

	if (screen === 'error') {
		// TODO: update this to use the loom signup link cause right now it only sends the cloud one
		// can just add a 'bundle=loom' query param to the link
		return <ErrorView />;
	}

	return (
		<Box testId="after-onboarding-step-provisioning">
			<Stack space="space.150" alignInline="center">
				<Heading size="large">Provisioning</Heading>
				<Text>Provisioning in progress...</Text>
			</Stack>
		</Box>
	);
};
